/// TYPES ///
import { IShopArea } from "api/types/shop_quote/area";
import { IShopCounter } from "api/types/shop_quote/counter";
import { IShopLabel } from "api/types/shop_quote/label";
import { IShopQuote } from "api/types/shop_quote/quote";
import { User } from "api/types/authentication";
/// COMPONENTS ///
import POPrintHeader from "components/universal/print/header/po";
import PrintColorOptions from "components/universal/print/color_option";
import PrintLineItems from "components/universal/print/line_items";
import PrintSummaryHeaders from "components/universal/print/summary_headers";
import PrintSummary from "components/universal/print/summary";
import PrintTerms from "components/universal/print/terms";
import PrintSignature from "components/universal/print/signature";
import ShopQuotePrintAreas from "components/universal/diagrams/print_counter/shop_areas";
/// PDF ///
import { Page } from "@react-pdf/renderer";
import { Document } from "@react-pdf/renderer";
import { StyleSheet } from "@react-pdf/renderer";
import { Font } from "@react-pdf/renderer";
import { Image } from "@react-pdf/renderer";
/// IMAGE ///
import Logo from "static/logo_icon.png";
/// FUNCTIONS ///
import { containsColorOption } from "components/quote/color_options/validate";

interface Props {
  user: User,
  quote: IShopQuote,
  areas: IShopArea[],
  counters: IShopCounter[],
  labels: IShopLabel[],
  logo: string
}

export default function ShopQuotePrintPreviewBody({ areas, counters, labels, logo, quote, user }: Props) {
  Font.register({
    family: "Montserrat",
    fonts: [
      {
        src: "https://fonts.gstatic.com/s/montserrat/v26/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtr6Ew-Y3tcoqK5.ttf",
      },
      {
        src: "https://fonts.gstatic.com/s/montserrat/v26/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCu170w-Y3tcoqK5.ttf",
        fontWeight: "bold"
      }
    ]
  });

  const styles = StyleSheet.create({
    page: {
      paddingLeft: "40px",
      paddingRight: "40px",
      paddingTop: "40px",
      paddingBottom: "40px"
    },
    title: {
      fontWeight: "bold",
      fontSize: 14
    },
    body2: {
      fontSize: 14
    },
    logo: {
      height: "40px",
      width: "80px",
      objectFit: "contain",
      objectPosition: "left"
    },
    row: {
      marginTop: "25px",
      display: "flex",
      flexDirection: "row"
    },
    font: {
      fontFamily: "Montserrat"
    },
    footer_image: {
      position: "absolute",
      right: 10,
      bottom: 5,
      height: "20px",
      width: "20px"
    }
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <POPrintHeader
          title="ORDER ESTIMATE"
          number={quote?.estimate_number ?? ""}
          logo_url={logo ? logo : ""}
          bill_to_name={quote?.customer_data?.is_business ? quote?.customer_data?.business_name ?? "" : `${quote?.customer_data?.first_name ?? ""} ${quote?.customer_data?.last_name ?? ""}`}
          bill_to_address={quote?.customer_data?.address ?? ""}
          bill_to_city={quote?.customer_data?.city ?? ""}
          bill_to_province={quote?.customer_data?.province ?? ""}
          bill_to_postal_code={quote?.customer_data?.postal_code ?? ""}
          bill_to_phone_number={quote?.customer_data?.phone_number ?? ""}
          bill_to_email={quote?.customer_data?.email ?? ""}
          vendor_name={user?.business_name ?? ""}
          vendor_address={user?.address ?? ""}
          vendor_city={user?.city ?? ""}
          vendor_province={user?.province ?? ""}
          vendor_postal_code={user?.postal_code ?? ""}
          vendor_phone_number={user?.phone_number ?? ""}
          vendor_email={user?.email ?? ""}
          job_site_address={quote?.job_data?.geo_address ?? ""}
          job_site_city={""}
          job_site_province={""}
          job_site_postal_code={""}
          job_site_phone_number={quote?.job_data?.phone_number ?? ""}
          job_site_email={quote?.job_data?.email ?? ""}
          date={quote?.date_created ?? ""} />
        <PrintSummaryHeaders />
        <PrintLineItems areas={areas ?? []} />
        <PrintSummary
          total={quote?.total ?? 0}
          tax_name={quote?.tax_name ?? "Tax"}
          tax={quote?.tax ?? 0}
          second_tax_name={quote?.second_tax_name ?? "Tax2"}
          second_tax={quote?.second_tax ?? 0}
          discount={quote?.discount ?? 0} />
        {
          containsColorOption(areas) ?
            <PrintColorOptions areas={areas} /> :
            null
        }
        <PrintTerms payment_terms={user?.payment_terms ?? ""} />
        <ShopQuotePrintAreas counters={counters} areas={areas} labels={labels} />
        <PrintSignature signature={quote?.signature_image} name={`${quote?.customer_data?.first_name ?? ""} ${quote?.customer_data?.last_name ?? ""}`} date={quote?.signature_date} />
        <Image src={Logo} style={styles.footer_image} fixed />
      </Page>
    </Document>
  );
}
